import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
// @ts-ignore
import styles from '../../CustomerDashboard.module.scss?module';
import { QuickOrderItem } from '../../types';
import { addBasketPositions } from '../addBasketPositions';
import { ProductSizeInterface } from '../../../../../../Interface/ProductSize';
import { getRequest } from '../../../../../Helper/ApiClient';
import { useProductList } from '../../../../../Context/ProductListContext';
import { useUser } from '../../../../../Context/UserContext';
import { formatCurrencyEur } from '../../../../../Helper/formatter';
import Link from '../../../../../Abstracts/Link/Link';
import Button from '../../../../../Abstracts/Button/Button';
import LoadingSpinner from '../../../../../Abstracts/LoadingSpinner/LoadingSpinner';
import ProductSizePrice from '../../../../../Abstracts/ProductSize/Abstracts/ProductSizePrice/ProductSizePrice';
import DepositInfo from '../../../../../Abstracts/Products/Abstracts/DepositInfo/DepositInfo';
import QuickOrderFlavourRow from './QuickOrderFlavourRow/QuickOrderFlavourRow';

// How long the button shows its success state, as the provider's addToBasket does.
const SUCCESS_RESET_MS = 2500;

interface QuickOrderProductProps {
  item: QuickOrderItem;
  /** Set where the product opens below its own row, which already names it
      and shows its price (the price change list): the head keeps the meta
      line and the deposit hint, and shows the price only when a promotion or
      clearance makes it differ from the regular price. The row is no link
      (a tap on it opens this block), so the meta line ends with the link to
      the product page (Marketing 04.11.2026). */
  belowRow?: boolean;
}

/* The picked product: head with brand, name, one meta line (category · size ·
   units per case; Marketing 21.10.2026, no MSRP), the deposit hint and price, the flavour rows, and "Add to basket".

   Must render inside the dashboard's ProductListProvider (see CustomerDashboard):
   the flavour rows write their quantities there. The button posts them through
   addBasketPositions instead of the provider's addToBasket (see there for why),
   and only the quantities of this product. Each successful flavour is cleared,
   a failed one keeps its quantity. */
export default function QuickOrderProduct({
  item,
  belowRow = false,
}: QuickOrderProductProps) {
  const { flavoursToAdd, removeFlavour, getUnits } = useProductList();
  const { basketInfo, fetchUser } = useUser();
  const [pending, setPending] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [failedCount, setFailedCount] = useState<number>(0);
  // The posts outlive the render that started them: the run compares against the
  // current quantities, so a field changed meanwhile keeps its new value.
  const flavoursRef = useRef(flavoursToAdd);
  const pendingRef = useRef<boolean>(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    flavoursRef.current = flavoursToAdd;
  }, [flavoursToAdd]);

  useEffect(() => () => clearTimeout(successTimeoutRef.current), []);

  // The contexts of the product page: flavours with stock and customer prices.
  // product_size_list also carries category, vpe, unit and price.
  const productRequest = getRequest<ProductSizeInterface[]>(
    `/api/onlineshop/product-sizes?product_size_ids=${item.product_size_erp_id}&context[]=product_attributes&context[]=product_size_list&context[]=clearance_price`
  );

  // A dead session redirects to the login page, so the data can be an HTML string.
  const product = Array.isArray(productRequest.data)
    ? productRequest.data[0]
    : undefined;
  const flavours = product?.product_attributes ?? [];
  const flavourIds = flavours.map((flavour) => flavour.erp_id);

  const addProductToBasket = async () => {
    // A second click before the re-render must not start a second run.
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    clearTimeout(successTimeoutRef.current);
    setPending(true);
    setSuccess(false);
    setFailedCount(0);

    // The provider can still hold quantities the dealer no longer sees, e.g. of
    // a product picked before; only the flavours of this product go out.
    const snapshot = flavoursToAdd.filter(({ flavourId }) =>
      flavourIds.includes(flavourId)
    );
    const { succeeded, failed } = await addBasketPositions(
      snapshot.map(({ flavourId, quantity }) => ({ flavourId, quantity }))
    );
    succeeded.forEach((flavourId) => {
      const sent = snapshot.find((flavour) => flavour.flavourId === flavourId);
      const current = flavoursRef.current.find(
        (flavour) => flavour.flavourId === flavourId
      );
      if (sent && current?.quantity === sent.quantity) {
        removeFlavour(flavourId);
      }
    });

    // Once for the whole run: the basket counter in the header.
    fetchUser();
    pendingRef.current = false;
    setPending(false);
    setFailedCount(failed);
    if (failed === 0) {
      setSuccess(true);
      successTimeoutRef.current = setTimeout(
        () => setSuccess(false),
        SUCCESS_RESET_MS
      );
    }
  };

  const productLink = `/products?brand_ids=${item.brand_erp_id}&product_size_ids=${item.product_size_erp_id}`;

  return (
    <>
      <div className={styles.productHead}>
        <div className={styles.productHeadInfo}>
          {!belowRow && (
            <div>
              <span className={styles.brand}>{item.brand_name}</span>{' '}
              <Link to={productLink} className={styles.productLink}>
                {item.product_name}
              </Link>
            </div>
          )}
          {product && (
            <div className={styles.productMeta}>
              {productMeta(product)}
              {belowRow && (
                <>
                  {' · '}
                  <Link
                    to={productLink}
                    inheritColor={false}
                    className={`${styles.link} text-nowrap`}
                  >
                    View product
                  </Link>
                </>
              )}
            </div>
          )}
          {/* The deposit hint stays, as the product list shows it (review 21.10.2026). */}
          {product &&
            product.single_deposit_price > 0 &&
            product.deposit_units_count > 0 && (
              <DepositInfo
                unitsCount={product.deposit_units_count}
                price={product.single_deposit_price}
                className={styles.productMeta}
              />
            )}
        </div>
        {/* ProductSizePrice has no empty state: without a customer price it
            would still draw its price block. */}
        {product?.price != null &&
          (!belowRow || product.price_show_regular || product.price_is_red) && (
            <ProductSizePrice product_size={product} />
          )}
      </div>
      <div className={styles.flavours}>
        {productRequest.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : productRequest.isError || !product ? (
          <div className={styles.caption}>Product not available right now</div>
        ) : flavours.length === 0 ? (
          <div className={styles.caption}>No flavours available</div>
        ) : (
          flavours.map((flavour) => (
            <QuickOrderFlavourRow
              key={flavour.erp_id}
              product_attribute={flavour}
              product_size={product}
            />
          ))
        )}
      </div>
      {flavours.length > 0 && (
        <div className={styles.footer}>
          {/* One amount, the one the basket shows (Marketing 20.10.2026). */}
          <span className={styles.footerTotals}>
            Basket: {formatCurrencyEur(basketInfo?.basket_total_price ?? 0)}
          </span>
          <Button
            type="button"
            disabled={pending || getUnits(flavourIds) === 0}
            loading={pending}
            success={success}
            onClick={addProductToBasket}
          >
            Add to basket
          </Button>
        </div>
      )}
      {failedCount > 0 && (
        <p className={styles.footerError} role="alert">
          {failedCount === 1
            ? '1 flavour could not be added, its quantity is kept'
            : `${failedCount} flavours could not be added, their quantities are kept`}
        </p>
      )}
    </>
  );
}

function productMeta(product: ProductSizeInterface): string {
  const segments: string[] = [];
  if (product.category?.name) {
    segments.push(product.category.name);
  }
  if (product.unit_value) {
    segments.push(`${product.unit_value} ${product.unit_name ?? ''}`.trim());
  }
  if (product.vpe > 0) {
    segments.push(
      product.vpe === 1 ? '1 pc per case' : `${product.vpe} pcs per case`
    );
  }
  return segments.join(' · ');
}
